import { useState, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { Menu, X } from "lucide-react";
import Slash from "./Slash";
import { usePageTransition } from "./PageTransition";
import { scrollToSection } from "../lib/scroll";

const links = [
  { label: "Protocol", target: "science" },
  { label: "Arsenal", target: "products" },
  { label: "Lab", target: "lab", route: "/lab" },
  { label: "Members", target: "members", route: "/members" },
  { label: "Contact", target: "contact" },
];

const mobileLinks = [
  { label: "Protocol", target: "science" },
  { label: "Arsenal", target: "products" },
  { label: "Lab", target: "lab", route: "/lab" },
  { label: "Members", target: "members", route: "/members" },
  { label: "Contact", target: "contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");
  const transition = usePageTransition();

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const ids = ["hero", "science", "products", "lab", "contact"];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id === "hero" ? "" : e.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const go = (id, route) => {
    setOpen(false);
    if (route) {
      transition ? transition.go(route) : (window.location.href = route);
      return;
    }
    scrollToSection(id);
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled || open ? "bg-black/70 backdrop-blur-xl border-b border-[#1c1c1c]" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 md:px-12">
        <button
          data-testid="logo-home"
          onClick={() => (window.__lenis ? window.__lenis.scrollTo(0, { duration: 1.4 }) : window.scrollTo({ top: 0, behavior: "smooth" }))}
          className="flex items-center gap-3"
        >
          <span className="grid h-8 w-8 place-items-center border border-[#d91616]/60 text-[#d91616]">
            <span className="font-jp text-lg leading-none">藤</span>
          </span>
          <span className="font-display text-xl font-extrabold tracking-tight">
            TOJI<span className="text-[#d91616]">.</span>
          </span>
        </button>

        <div className="hidden items-center gap-10 md:flex">
          {links.map((l) => (
            <button
              key={l.target}
              data-testid={`nav-${l.target}`}
              onClick={() => go(l.target, l.route)}
              className={`slash-host relative font-mono2 px-1 py-1 text-xs uppercase tracking-[0.2em] transition-colors ${
                active === l.target ? "text-white" : "text-white/60 hover:text-white"
              }`}
            >
              {l.label}
              <Slash />
              <span
                className={`absolute -bottom-1 left-1 h-[2px] bg-[#d91616] transition-all duration-300 ${
                  active === l.target ? "w-5 opacity-100" : "w-0 opacity-0"
                }`}
              />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cta"
            onClick={() => go("contact")}
            className="slash-host hidden border border-[#d91616] bg-[#d91616] px-5 py-2 font-mono2 text-xs uppercase tracking-[0.2em] text-white transition-colors hover:bg-transparent hover:text-[#d91616] sm:block"
          >
            Get Access
            <Slash />
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="grid h-10 w-10 place-items-center border border-[#2a2a2a] text-white md:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </nav>

      <motion.div
        style={{ scaleX }}
        data-testid="scroll-progress"
        className="absolute bottom-0 left-0 right-0 h-[2px] origin-left bg-gradient-to-r from-[#7a0000] via-[#d91616] to-[#ff4d4d]"
      />

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden md:hidden"
          >
            <div className="flex flex-col gap-1 px-6 pb-6 pt-2">
              {mobileLinks.map((l) => (
                <motion.button
                  key={l.target}
                  data-testid={`nav-mobile-${l.target}`}
                  onClick={() => go(l.target, l.route)}
                  whileTap={{ scale: 0.95 }}
                  className="slash-host text-glow-hover group relative overflow-hidden border-b border-[#161616] py-4 text-left font-display text-2xl font-extrabold tracking-tight text-white transition-colors hover:text-[#d91616]"
                >
                  {l.label}
                  <span className="pulse-bloom z-0" aria-hidden="true" />
                  <Slash />
                </motion.button>
              ))}
              <motion.button
                data-testid="nav-mobile-cta"
                onClick={() => go("contact")}
                whileTap={{ scale: 0.95 }}
                className="slash-host mt-4 bg-[#d91616] py-3 text-center font-mono2 text-xs uppercase tracking-[0.2em] text-white"
              >
                Get Access
                <Slash />
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
